import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import WysiwygEditor from '@/components/WysiwygEditor';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/hooks/useLanguage';
import { useProductTranslation, SupportedLanguage } from '@/hooks/useProductTranslation';
import { Languages, Loader2, Check, AlertCircle } from 'lucide-react';

interface ProductTranslationsFormProps {
  titleTranslations: Record<string, string>;
  descriptionTranslations: Record<string, string>;
  onTitleChange: (translations: Record<string, string>) => void;
  onDescriptionChange: (translations: Record<string, string>) => void;
  sourceLang?: SupportedLanguage;
  productId?: string;
}

const LANGUAGE_FLAGS = {
  'fr': '🇫🇷',
  'en': '🇬🇧',
  'lg-fr': '🇨🇩',
  'sw': '🇹🇿'
};

const LANGUAGE_NAMES = {
  'fr': 'Français',
  'en': 'English',
  'lg-fr': 'Lingala',
  'sw': 'Swahili'
};

export const ProductTranslationsForm = ({
  titleTranslations,
  descriptionTranslations,
  onTitleChange,
  onDescriptionChange,
  sourceLang = 'fr',
  productId = 'temp-' + Date.now()
}: ProductTranslationsFormProps) => {
  const { t, language } = useLanguage();
  const { translateAllLanguages, isTranslating, error } = useProductTranslation();
  
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(sourceLang);
  const [localTitle, setLocalTitle] = useState<Record<string, string>>(titleTranslations);
  const [localDescription, setLocalDescription] = useState<Record<string, string>>(descriptionTranslations);
  const [translationSuccess, setTranslationSuccess] = useState(false);

  // Sync with props
  useEffect(() => {
    setLocalTitle(titleTranslations);
    setLocalDescription(descriptionTranslations);
  }, [titleTranslations, descriptionTranslations]);

  const handleAutoTranslate = async () => {
    const sourceTitle = localTitle[sourceLang] || '';
    const sourceDescription = localDescription[sourceLang] || '';

    if (!sourceTitle.trim()) {
      return;
    }

    setTranslationSuccess(false);

    try {
      const { titleTranslations: newTitles, descriptionTranslations: newDescs } = 
        await translateAllLanguages(sourceTitle, sourceDescription, productId);

      // Mettre à jour les états locaux
      setLocalTitle(newTitles);
      setLocalDescription(newDescs);
      
      // Notifier le parent
      onTitleChange(newTitles);
      onDescriptionChange(newDescs);

      setTranslationSuccess(true);
      
      // Cacher le message de succès après 3 secondes
      setTimeout(() => setTranslationSuccess(false), 3000);
    } catch (err) {
      console.error('Auto-translation error:', err);
    }
  };

  const handleTitleChange = (lang: SupportedLanguage, value: string) => {
    const newTitles = { ...localTitle, [lang]: value };
    setLocalTitle(newTitles);
    onTitleChange(newTitles);
  };

  const handleDescriptionChange = (lang: SupportedLanguage, value: string) => {
    const newDescriptions = { ...localDescription, [lang]: value };
    setLocalDescription(newDescriptions);
    onDescriptionChange(newDescriptions);
  };

  const getLanguageStatus = (lang: SupportedLanguage) => {
    const hasTitle = localTitle[lang] && localTitle[lang].trim() !== '';
    const hasDescription = localDescription[lang] && localDescription[lang].trim() !== '';
    
    if (lang === sourceLang) return 'source';
    if (hasTitle && hasDescription) return 'complete';
    if (hasTitle) return 'partial';
    return 'missing';
  };

  return (
    <div className="space-y-4">
      {/* Header avec bouton de traduction auto */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages className="h-5 w-5" />
          <h3 className="font-semibold">{t('product.multilingualInfo')}</h3>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAutoTranslate}
          disabled={isTranslating || !localTitle[sourceLang]}
          className="gap-2"
        >
          {isTranslating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('product.translating')}
            </>
          ) : (
            <>
              <Languages className="h-4 w-4" />
              {t('product.autoTranslate')}
            </>
          )}
        </Button>
      </div>

      {/* Afficher les messages */}
      {translationSuccess && (
        <Alert>
          <Check className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-600">
            {t('product.translationSuccessMessage') || 'Traductions générées avec succès !'}
          </AlertDescription>
        </Alert>
      )}
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Info sur la langue source */}
      <Alert>
        <AlertDescription className="text-sm">
          {t('product.translationHint')}
        </AlertDescription>
      </Alert>

      {/* Tabs par langue */}
      <Tabs value={currentLang} onValueChange={(v) => setCurrentLang(v as SupportedLanguage)}>
        <TabsList className="grid w-full grid-cols-4">
          {(['fr', 'en', 'lg-fr', 'sw'] as SupportedLanguage[]).map((lang) => {
            const status = getLanguageStatus(lang);
            return (
              <TabsTrigger key={lang} value={lang} className="gap-1.5">
                <span>{LANGUAGE_FLAGS[lang]}</span>
                <span className="hidden sm:inline">{LANGUAGE_NAMES[lang]}</span>
                {status === 'complete' && <Check className="h-3 w-3 text-green-500" />}
                {status === 'partial' && <AlertCircle className="h-3 w-3 text-orange-500" />}
                {status === 'source' && <Badge variant="default" className="ml-1 h-4 px-1 text-[10px]">Source</Badge>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {(['fr', 'en', 'lg-fr', 'sw'] as SupportedLanguage[]).map((lang) => (
          <TabsContent key={lang} value={lang} className="space-y-4">
            <div>
              <Label htmlFor={`title-${lang}`}>
                {t('product.name')} ({LANGUAGE_NAMES[lang]})
                {lang === sourceLang && <Badge variant="outline" className="ml-2">{t('product.sourceLanguage')}</Badge>}
              </Label>
              <Input
                id={`title-${lang}`}
                value={localTitle[lang] || ''}
                onChange={(e) => handleTitleChange(lang, e.target.value)}
                placeholder={`${t('product.namePlaceholder')}... (${LANGUAGE_NAMES[lang]})`}
                required={lang === sourceLang}
              />
            </div>

            <div>
              <Label htmlFor={`description-${lang}`}>
                {t('product.description')} ({LANGUAGE_NAMES[lang]})
              </Label>
              <WysiwygEditor
                value={localDescription[lang] || ''}
                onChange={(value) => handleDescriptionChange(lang, value)}
                placeholder={`${t('product.descriptionPlaceholder')}... (${LANGUAGE_NAMES[lang]})`}
                className="min-h-[200px]"
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Indicateur de statut */}
      <div className="flex gap-2 text-xs text-muted-foreground">
        <Check className="h-4 w-4 text-green-500" />
        <span>{t('product.translationComplete')}</span>
        <AlertCircle className="h-4 w-4 text-orange-500 ml-2" />
        <span>{t('product.translationPartial')}</span>
      </div>
    </div>
  );
};
